import firebase from '../config/firebase';
require('firebase/auth')
const db = firebase.firestore();

function normalizeCnpjDigits(cnpj) {
    return String(cnpj || "").replace(/\D/g, "");
}

function formatCnpjMask(cnpj) {
    const digits = normalizeCnpjDigits(cnpj);
    if (digits.length !== 14) return digits;
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5",
    );
}

function shipperIsTransport(shipper) {
    if (!shipper || typeof shipper !== "object") return false;
    const value = shipper.isTransport;
    return (
        value === true ||
        value === 1 ||
        String(value).toLowerCase() === "true"
    );
}

const shipperService = {

    normalizeCnpjDigits,
    formatCnpjMask,

    async getShipperAvailable(id){

        return db
            .collection('shipper')
            .doc(id)
            .get();
    },

    async getShipperAllbyCNPJ(cnpj) {
        const shipperQuerySnapshot = await db
            .collection('shipper')
            .where("dataPersonal.documentNumber", "==", cnpj)
            .get();

        if (shipperQuerySnapshot.empty) {
            return null;
            // throw new Error("Transportadora não encontrada.");
        }

        // Assumindo que o CNPJ é único, pegue o primeiro documento
        const shipperDoc = shipperQuerySnapshot.docs[0];
        
        return shipperDoc.data();
    },

    /**
     * Busca transportadora (isTransport) pelo CNPJ.
     * Embarcadores com o mesmo CNPJ são ignorados.
     */
    async getTransportadoraByCNPJ(cnpj) {
        const digits = normalizeCnpjDigits(cnpj);
        if (digits.length !== 14) return null;

        const variants = Array.from(
            new Set([digits, formatCnpjMask(digits)].filter(Boolean)),
        );

        for (const variant of variants) {
            const snapshot = await db
                .collection("shipper")
                .where("dataPersonal.documentNumber", "==", variant)
                .get();

            const match = snapshot.docs.find((doc) =>
                shipperIsTransport(doc.data()),
            );

            if (match) {
                return { id: match.id, ...match.data() };
            }
        }

        // Fallback: CNPJ pode estar salvo em formato diferente.
        try {
            const transportSnapshot = await db
                .collection("shipper")
                .where("isTransport", "==", true)
                .get();

            const match = transportSnapshot.docs.find((doc) => {
                const stored = normalizeCnpjDigits(
                    doc.data()?.dataPersonal?.documentNumber,
                );
                return stored === digits;
            });

            if (match) {
                return { id: match.id, ...match.data() };
            }
        } catch (error) {
            console.error(
                "Fallback getTransportadoraByCNPJ (isTransport):",
                error,
            );
        }

        return null;
    },

    /**
     * Embarcadores (empresas) que têm a transportadora como afiliada/cliente.
     * Fonte principal: shipper.affiliatedCarrierIds.
     * Fallback: user_web.uidShipperAffiliates (sem viewALL).
     */
    async getClientEmbarcadoresByCarrierId(carrierId) {
        const carrierUid = String(carrierId || "").trim();
        if (!carrierUid) return [];

        const embarcadorIds = new Set();

        try {
            const commercialSnap = await db
                .collection("shipper")
                .where("affiliatedCarrierIds", "array-contains", carrierUid)
                .get();
            commercialSnap.docs.forEach((doc) => {
                if (doc.id && doc.id !== carrierUid) embarcadorIds.add(doc.id);
            });
        } catch (error) {
            console.error(
                "getClientEmbarcadoresByCarrierId affiliatedCarrierIds:",
                error,
            );
        }

        const usersSnapshot = await db
            .collection("user_web")
            .where("uidShipperAffiliates", "array-contains", carrierUid)
            .get();

        usersSnapshot.docs.forEach((doc) => {
            const data = doc.data() || {};
            if (data.viewALL === true) return;
            const uid = String(data.uidShipper || "").trim();
            if (uid && uid !== carrierUid) embarcadorIds.add(uid);
        });

        if (!embarcadorIds.size) return [];

        const docs = await Promise.all(
            Array.from(embarcadorIds).map((shipperId) =>
                db.collection("shipper").doc(shipperId).get(),
            ),
        );

        return docs
            .filter((doc) => doc.exists)
            .map((doc) => {
                const data = doc.data() || {};
                const personal = data.dataPersonal || {};
                return {
                    id: doc.id,
                    socialName: personal.socialName || "",
                    nameFantasy: personal.nameFantasy || "",
                    documentNumber: personal.documentNumber || "",
                    isTransport: shipperIsTransport(data),
                };
            })
            .filter((item) => !item.isTransport)
            .sort((a, b) =>
                String(a.nameFantasy || a.socialName).localeCompare(
                    String(b.nameFantasy || b.socialName),
                    "pt-BR",
                ),
            );
    },

    async getShipperbyPrincipal() {
        const shipperQuerySnapshot = await db
            .collection('shipper')
            .where("isPrincipal", "==", true)
            .get();

        if (shipperQuerySnapshot.empty) {
            return null;
            // throw new Error("Transportadora não encontrada.");
        }

        const shipperDoc = shipperQuerySnapshot.docs[0];

        return {
            id: shipperDoc.id,
            ...shipperDoc.data()
        };
    },

    async getShipperUid(cnpj) {
        
        const query = await db.collection('shipper')
            .where("dataPersonal.documentNumber", "==", cnpj)
            .get();

        if (query.empty) {
            return null;
            // throw new Error("Transportadora não encontrada.");
        }

        const shipperDoc = query.docs[0];
        
        return { uid: shipperDoc.id }
    },

    async deleteShipper(id) {
        if (!id) {
            throw new Error("ID do embarcador é obrigatório para exclusão.");
        }

        await db.collection('shipper').doc(id).delete();
    },

    /**
     * Vincula transportadora ao embarcador (relação comercial para Lances).
     * Grava em shipper.affiliatedCarrierIds do embarcador.
     */
    async appendAffiliatedCarrier(embarcadorShipperId, carrierShipperId) {
        const embId = String(embarcadorShipperId || "").trim();
        const carId = String(carrierShipperId || "").trim();

        if (!embId) {
            throw new Error("Embarcador inválido para vínculo comercial.");
        }
        if (!carId) {
            throw new Error("Transportadora inválida para vínculo comercial.");
        }
        if (embId === carId) {
            throw new Error(
                "Embarcador e transportadora não podem ser o mesmo cadastro.",
            );
        }

        const ref = db.collection("shipper").doc(embId);
        const snap = await ref.get();
        if (!snap.exists) {
            throw new Error("Embarcador não encontrado.");
        }

        const current = Array.isArray(snap.data()?.affiliatedCarrierIds)
            ? snap.data().affiliatedCarrierIds
            : [];
        const next = Array.from(
            new Set(
                [...current, carId]
                    .map((uid) => String(uid || "").trim())
                    .filter(Boolean),
            ),
        );

        await ref.update({ affiliatedCarrierIds: next });
        return next;
    },

    /**
     * Lista embarcadores (não transportadora) para seleção de vínculo comercial.
     */
    async listEmbarcadoresForCarrierLink(excludeShipperId) {
        const excludeId = String(excludeShipperId || "").trim();
        const snapshot = await db.collection("shipper").get();

        return snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((item) => {
                if (excludeId && item.id === excludeId) return false;
                return !shipperIsTransport(item);
            })
            .map((item) => {
                const personal = item.dataPersonal || {};
                return {
                    id: item.id,
                    socialName: personal.socialName || "",
                    nameFantasy: personal.nameFantasy || "",
                    documentNumber: personal.documentNumber || "",
                };
            })
            .sort((a, b) =>
                String(a.nameFantasy || a.socialName).localeCompare(
                    String(b.nameFantasy || b.socialName),
                    "pt-BR",
                ),
            );
    },

}

export default shipperService;
